import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  ClipboardList, Calendar, AlertTriangle, Users, CheckCircle2,
  Plus, Search, Edit2, Trash2, Clock, ArrowLeft, RefreshCw,
  BookOpen, CalendarCheck, User, ChevronRight, X, Filter
} from 'lucide-react';
import { toast } from 'sonner';

// Priority badge colors
const PRIORITY_COLORS = {
  High: 'bg-red-100 text-red-700 border-red-200',
  Medium: 'bg-amber-100 text-amber-700 border-amber-200',
  Low: 'bg-slate-100 text-slate-600 border-slate-200'
};

// Status badge colors
const STATUS_COLORS = {
  Pending: 'bg-blue-100 text-blue-700',
  'In Progress': 'bg-purple-100 text-purple-700',
  Completed: 'bg-emerald-100 text-emerald-700',
  Done: 'bg-emerald-100 text-emerald-700'
};

// Format date for display
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

// Check if date is overdue
const isOverdue = (dateStr, status) => {
  if (!dateStr || status === 'Completed' || status === 'Done') return false;
  return new Date(dateStr) < new Date();
};

// Check if date is today
const isToday = (dateStr) => {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  const today = new Date();
  return date.toDateString() === today.toDateString();
};

// ==================== TASKS SECTION ====================
const TasksSection = ({ user, initialFilter }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ 
    open_tasks: 0, due_today: 0, overdue: 0, team_pending: 0,
    followups_due_today: 0, followups_overdue: 0, open_followups: 0
  });
  const [teamMembers, setTeamMembers] = useState([]);
  
  // Filters
  const [assignedTo, setAssignedTo] = useState('me');
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [search, setSearch] = useState('');
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  
  // Form state
  const [form, setForm] = useState({
    title: '',
    description: '',
    assigned_to_user_id: '',
    due_date: '',
    priority: 'Medium',
    related_type: '',
    related_name: '',
    notes: ''
  });

  // Apply initial filter from URL
  useEffect(() => {
    if (initialFilter === 'overdue') {
      setDueDate('overdue');
      setAssignedTo('me');
      setStatus('');
    } else if (initialFilter === 'today') {
      setDueDate('today');
      setAssignedTo('me');
      setStatus('');
    }
  }, [initialFilter]);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params = { assigned_to: assignedTo };
      if (status) params.status = status;
      if (priority) params.priority = priority;
      if (dueDate) params.due_date = dueDate;
      if (search) params.search = search;
      
      const response = await api.getManagerTasks(params);
      setTasks(response.data.tasks || []);
    } catch (error) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [assignedTo, status, priority, dueDate, search]);

  const fetchSummary = async () => {
    try {
      const response = await api.getManagerTasksSummary();
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to load summary');
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await api.getManagerTeamMembers();
      setTeamMembers(response.data.members || []);
    } catch (error) {
      console.error('Failed to load team members');
    }
  };

  useEffect(() => {
    fetchTasks();
    fetchSummary();
    fetchTeamMembers();
  }, [fetchTasks]);

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      toast.error('Task title is required');
      return;
    }
    
    try {
      if (editingTask) {
        await api.updateManagerTask(editingTask.task_id, form);
        toast.success('Task updated');
      } else {
        await api.createManagerTask(form);
        toast.success('Task created');
      }
      setDialogOpen(false);
      setEditingTask(null);
      setForm({ title: '', description: '', assigned_to_user_id: '', due_date: '', priority: 'Medium', related_type: '', related_name: '', notes: '' });
      fetchTasks();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save task');
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setForm({
      title: task.title || '',
      description: task.description || '',
      assigned_to_user_id: task.assigned_to_user_id || '',
      due_date: task.due_date ? task.due_date.split('T')[0] : '',
      priority: task.priority || 'Medium',
      status: task.status || 'Pending',
      related_type: task.related_type || '',
      related_name: task.related_name || '',
      notes: task.notes || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!taskToDelete) return;
    try {
      await api.deleteManagerTask(taskToDelete.task_id);
      toast.success('Task deleted');
      setDeleteDialogOpen(false);
      setTaskToDelete(null);
      fetchTasks();
      fetchSummary();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const handleStatusChange = async (task, newStatus) => {
    try {
      await api.updateManagerTask(task.task_id, { status: newStatus });
      toast.success(`Task marked as ${newStatus}`);
      fetchTasks();
      fetchSummary();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Quick filter handlers - apply filters and refresh
  const applyQuickFilter = (filter) => {
    if (filter === 'mine') {
      setAssignedTo('me');
      setStatus('');
      setDueDate('');
    } else if (filter === 'team') {
      setAssignedTo('team');
      setStatus('');
      setDueDate('');
    } else if (filter === 'overdue') {
      setAssignedTo('me');
      setDueDate('overdue');
      setStatus('');
    } else if (filter === 'today') {
      setAssignedTo('me');
      setDueDate('today');
      setStatus('');
    } else if (filter === 'completed') {
      setAssignedTo('me');
      setStatus('Completed');
      setDueDate('');
    }
  };

  return (
    <div className="space-y-4">
      {/* Summary Cards - Clickable */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card 
          className={`p-4 cursor-pointer hover:shadow-md transition-shadow ${assignedTo === 'me' && !status && !dueDate ? 'ring-2 ring-blue-500' : ''}`}
          onClick={() => applyQuickFilter('mine')}
          data-testid="summary-open-tasks"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ClipboardList size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{summary.open_tasks}</p>
              <p className="text-xs text-slate-500">Open Tasks</p>
            </div>
          </div>
        </Card>
        
        <Card 
          className={`p-4 cursor-pointer hover:shadow-md transition-shadow ${dueDate === 'today' ? 'ring-2 ring-amber-500' : ''}`}
          onClick={() => applyQuickFilter('today')}
          data-testid="summary-due-today"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Clock size={20} className="text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{summary.due_today}</p>
              <p className="text-xs text-slate-500">Tasks Due Today</p>
            </div>
          </div>
        </Card>
        
        <Card 
          className={`p-4 cursor-pointer hover:shadow-md transition-shadow ${dueDate === 'overdue' ? 'ring-2 ring-red-500' : ''}`}
          onClick={() => applyQuickFilter('overdue')}
          data-testid="summary-overdue"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle size={20} className="text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{summary.overdue}</p>
              <p className="text-xs text-slate-500">Overdue</p>
            </div>
          </div>
        </Card>
        
        <Card 
          className={`p-4 cursor-pointer hover:shadow-md transition-shadow ${assignedTo === 'team' ? 'ring-2 ring-purple-500' : ''}`}
          onClick={() => applyQuickFilter('team')}
          data-testid="summary-team-pending"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Users size={20} className="text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{summary.team_pending}</p>
              <p className="text-xs text-slate-500">Team Pending</p>
            </div>
          </div>
        </Card>
        
        {/* Followups Due Today - Links to Followups tab */}
        <Card 
          className="p-4 cursor-pointer hover:shadow-md transition-shadow border-cyan-200"
          onClick={() => window.location.href = '/manager-desk?tab=followups&filter=today'}
          data-testid="summary-followups-today"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-cyan-100 rounded-lg">
              <CalendarCheck size={20} className="text-cyan-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{summary.followups_due_today || 0}</p>
              <p className="text-xs text-slate-500">Followups Today</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Quick Filter Chips */}
      <div className="flex flex-wrap gap-2">
        <Badge 
          variant={assignedTo === 'me' && !status && !dueDate ? 'default' : 'outline'}
          className="cursor-pointer px-3 py-1"
          onClick={() => applyQuickFilter('mine')}
        >
          Mine
        </Badge>
        <Badge 
          variant={assignedTo === 'team' ? 'default' : 'outline'}
          className="cursor-pointer px-3 py-1"
          onClick={() => applyQuickFilter('team')}
        >
          Team
        </Badge>
        <Badge 
          variant={dueDate === 'overdue' ? 'default' : 'outline'}
          className={`cursor-pointer px-3 py-1 ${dueDate === 'overdue' ? 'bg-red-600' : ''}`}
          onClick={() => applyQuickFilter('overdue')}
        >
          Overdue
        </Badge>
        <Badge 
          variant={dueDate === 'today' ? 'default' : 'outline'}
          className={`cursor-pointer px-3 py-1 ${dueDate === 'today' ? 'bg-amber-600' : ''}`}
          onClick={() => applyQuickFilter('today')}
        >
          Today
        </Badge>
        <Badge 
          variant={status === 'Completed' ? 'default' : 'outline'}
          className={`cursor-pointer px-3 py-1 ${status === 'Completed' ? 'bg-emerald-600' : ''}`}
          onClick={() => applyQuickFilter('completed')}
        >
          Completed
        </Badge>
      </div>

      {/* Filters and Add Button */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input 
            placeholder="Search tasks..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="task-search"
          />
        </div>
        
        <Select value={priority || "all"} onValueChange={(val) => setPriority(val === "all" ? "" : val)}>
          <SelectTrigger className="w-[140px]" data-testid="task-priority-filter">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priority</SelectItem>
            <SelectItem value="High">High</SelectItem>
            <SelectItem value="Medium">Medium</SelectItem>
            <SelectItem value="Low">Low</SelectItem>
          </SelectContent>
        </Select>
        
        <Button onClick={() => { setEditingTask(null); setForm({ title: '', description: '', assigned_to_user_id: '', due_date: '', priority: 'Medium', related_type: '', related_name: '', notes: '' }); setDialogOpen(true); }} data-testid="add-task-btn">
          <Plus size={16} className="mr-1" /> Add Task
        </Button>
      </div>

      {/* Tasks List */}
      <div className="space-y-2">
        {loading ? (
          <div className="text-center py-8">
            <RefreshCw className="animate-spin mx-auto mb-2 text-slate-400" size={24} />
            <p className="text-slate-500">Loading tasks...</p>
          </div>
        ) : tasks.length === 0 ? (
          <Card className="p-8 text-center">
            <CheckCircle2 size={40} className="mx-auto mb-2 text-emerald-500" />
            <p className="text-slate-600">No tasks found</p>
            <p className="text-sm text-slate-400">Create a new task to get started</p>
          </Card>
        ) : (
          <>
            {/* Desktop Table View */}
            <Card className="hidden md:block overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Task</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase w-32">Assigned To</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase w-24">Due Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase w-24">Priority</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase w-28">Status</th>
                    <th className="px-4 py-3 w-24"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {tasks.map((task) => (
                    <tr key={task.task_id} className={`hover:bg-slate-50 ${isOverdue(task.due_date, task.status) ? 'bg-red-50' : ''}`} data-testid={`task-row-${task.task_id}`}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-800">{task.title}</p>
                        {task.description && <p className="text-xs text-slate-500 truncate max-w-xs">{task.description}</p>}
                        {task.related_name && <span className="text-xs text-blue-600">{task.related_type}: {task.related_name}</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-slate-600">{task.assigned_to_name}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-sm ${isOverdue(task.due_date, task.status) ? 'text-red-600 font-medium' : isToday(task.due_date) ? 'text-amber-600 font-medium' : 'text-slate-600'}`}>
                          {formatDate(task.due_date)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className={`text-xs ${PRIORITY_COLORS[task.priority]}`}>
                          {task.priority}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Select value={task.status} onValueChange={(val) => handleStatusChange(task, val)}>
                          <SelectTrigger className={`h-7 text-xs ${STATUS_COLORS[task.status] || ''}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Pending">Pending</SelectItem>
                            <SelectItem value="In Progress">In Progress</SelectItem>
                            <SelectItem value="Completed">Completed</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(task)} data-testid={`edit-task-${task.task_id}`}>
                            <Edit2 size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => { setTaskToDelete(task); setDeleteDialogOpen(true); }} data-testid={`delete-task-${task.task_id}`}>
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-2">
              {tasks.map((task) => (
                <Card key={task.task_id} className={`p-4 ${isOverdue(task.due_date, task.status) ? 'border-red-200 bg-red-50' : ''}`} data-testid={`task-card-${task.task_id}`}>
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <p className="font-medium text-slate-800">{task.title}</p>
                      {task.description && <p className="text-xs text-slate-500 mt-1">{task.description}</p>}
                    </div>
                    <Badge variant="outline" className={`text-xs ${PRIORITY_COLORS[task.priority]}`}>
                      {task.priority}
                    </Badge>
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs text-slate-500 mb-3">
                    <span className="flex items-center gap-1">
                      <User size={12} /> {task.assigned_to_name}
                    </span>
                    <span className={`flex items-center gap-1 ${isOverdue(task.due_date, task.status) ? 'text-red-600' : ''}`}>
                      <Calendar size={12} /> {formatDate(task.due_date)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <Select value={task.status} onValueChange={(val) => handleStatusChange(task, val)}>
                      <SelectTrigger className={`h-8 w-32 text-xs ${STATUS_COLORS[task.status] || ''}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Pending">Pending</SelectItem>
                        <SelectItem value="In Progress">In Progress</SelectItem>
                        <SelectItem value="Completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(task)}>
                        <Edit2 size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => { setTaskToDelete(task); setDeleteDialogOpen(true); }}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Task Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingTask ? 'Edit Task' : 'New Task'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-slate-600 mb-1 block">Title *</label>
              <Input 
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Task title"
                data-testid="task-title-input"
              />
            </div>
            <div>
              <label className="text-sm text-slate-600 mb-1 block">Description</label>
              <Textarea 
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Task description"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-slate-600 mb-1 block">Assign To</label>
                <Select value={form.assigned_to_user_id || "none"} onValueChange={(val) => setForm({ ...form, assigned_to_user_id: val === "none" ? "" : val })}>
                  <SelectTrigger data-testid="task-assignee-select">
                    <SelectValue placeholder="Select person" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select person</SelectItem>
                    {teamMembers.map((m) => (
                      <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-slate-600 mb-1 block">Due Date</label>
                <Input 
                  type="date"
                  value={form.due_date}
                  onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                  data-testid="task-due-date-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-slate-600 mb-1 block">Priority</label>
                <Select value={form.priority} onValueChange={(val) => setForm({ ...form, priority: val })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Low">Low</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {editingTask && (
                <div>
                  <label className="text-sm text-slate-600 mb-1 block">Status</label>
                  <Select value={form.status} onValueChange={(val) => setForm({ ...form, status: val })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pending">Pending</SelectItem>
                      <SelectItem value="In Progress">In Progress</SelectItem>
                      <SelectItem value="Completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div>
              <label className="text-sm text-slate-600 mb-1 block">Notes</label>
              <Textarea 
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Additional notes"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} data-testid="save-task-btn">{editingTask ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{taskToDelete?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ==================== FOLLOWUPS SECTION ====================
const FollowupsSection = ({ user, initialFilter }) => {
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);
  
  // Filters
  const [status, setStatus] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [search, setSearch] = useState('');
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFollowup, setEditingFollowup] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [followupToDelete, setFollowupToDelete] = useState(null);
  
  // Form state
  const [form, setForm] = useState({
    customer_name: '',
    project_name: '',
    assigned_rep_id: '',
    assigned_rep_name: '',
    next_action: '',
    due_date: '',
    notes: ''
  });

  // Apply initial filter from URL
  useEffect(() => {
    if (initialFilter === 'overdue') {
      setDueDate('overdue');
      setStatus('');
    } else if (initialFilter === 'today') {
      setDueDate('today');
      setStatus('');
    }
  }, [initialFilter]);

  const fetchFollowups = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (dueDate) params.due_date = dueDate;
      if (search) params.search = search;
      
      const response = await api.getManagerFollowups(params);
      setFollowups(response.data.followups || []);
    } catch (error) {
      toast.error('Failed to load followups');
    } finally {
      setLoading(false);
    }
  }, [status, dueDate, search]);

  const fetchTeamMembers = async () => {
    try {
      const response = await api.getManagerTeamMembers();
      setTeamMembers(response.data.members || []);
    } catch (error) {
      console.error('Failed to load team members');
    }
  };

  useEffect(() => {
    fetchFollowups();
    fetchTeamMembers();
  }, [fetchFollowups]);

  const handleSubmit = async () => {
    if (!form.customer_name.trim() || !form.next_action.trim()) {
      toast.error('Customer name and next action are required');
      return;
    }
    
    try {
      // Get rep name if selected
      const rep = teamMembers.find(m => m.user_id === form.assigned_rep_id);
      const submitData = { ...form, assigned_rep_name: rep?.name || '' };
      
      if (editingFollowup) {
        await api.updateManagerFollowup(editingFollowup.followup_id, submitData);
        toast.success('Followup updated');
      } else {
        await api.createManagerFollowup(submitData);
        toast.success('Followup created');
      }
      setDialogOpen(false);
      setEditingFollowup(null);
      setForm({ customer_name: '', project_name: '', assigned_rep_id: '', assigned_rep_name: '', next_action: '', due_date: '', notes: '' });
      fetchFollowups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save followup');
    }
  };

  const handleEdit = (followup) => {
    setEditingFollowup(followup);
    setForm({
      customer_name: followup.customer_name || '',
      project_name: followup.project_name || '',
      assigned_rep_id: followup.assigned_rep_id || '',
      assigned_rep_name: followup.assigned_rep_name || '',
      next_action: followup.next_action || '',
      due_date: followup.due_date ? followup.due_date.split('T')[0] : '',
      status: followup.status || 'Pending',
      notes: followup.notes || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!followupToDelete) return;
    try {
      await api.deleteManagerFollowup(followupToDelete.followup_id);
      toast.success('Followup deleted');
      setDeleteDialogOpen(false);
      setFollowupToDelete(null);
      fetchFollowups();
    } catch (error) {
      toast.error('Failed to delete followup');
    }
  };

  const handleStatusChange = async (followup, newStatus) => {
    try {
      await api.updateManagerFollowup(followup.followup_id, { status: newStatus });
      toast.success(`Followup marked as ${newStatus}`);
      fetchFollowups();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters and Add Button */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input 
            placeholder="Search followups..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="followup-search"
          />
        </div>
        
        <Select value={status || "all"} onValueChange={(val) => setStatus(val === "all" ? "" : val)}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="Pending">Pending</SelectItem>
            <SelectItem value="Done">Done</SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={dueDate || "all"} onValueChange={(val) => setDueDate(val === "all" ? "" : val)}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Due Date" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Dates</SelectItem>
            <SelectItem value="today">Today</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
            <SelectItem value="this_week">This Week</SelectItem>
          </SelectContent>
        </Select>
        
        <Button onClick={() => { setEditingFollowup(null); setForm({ customer_name: '', project_name: '', assigned_rep_id: '', assigned_rep_name: '', next_action: '', due_date: '', notes: '' }); setDialogOpen(true); }} data-testid="add-followup-btn">
          <Plus size={16} className="mr-1" /> Add Followup
        </Button>
      </div>

      {/* Followups List */}
      <div className="space-y-2">
        {loading ? (
          <div className="text-center py-8">
            <RefreshCw className="animate-spin mx-auto mb-2 text-slate-400" size={24} />
            <p className="text-slate-500">Loading followups...</p>
          </div>
        ) : followups.length === 0 ? (
          <Card className="p-8 text-center">
            <CalendarCheck size={40} className="mx-auto mb-2 text-emerald-500" />
            <p className="text-slate-600">No followups found</p>
            <p className="text-sm text-slate-400">Create a followup to track customer actions</p>
          </Card>
        ) : (
          <>
            {/* Desktop Table View */}
            <Card className="hidden md:block overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Customer</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Project</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Rep</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Next Action</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase w-24">Due Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase w-24">Status</th>
                    <th className="px-4 py-3 w-24"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {followups.map((followup) => (
                    <tr key={followup.followup_id} className={`hover:bg-slate-50 ${isOverdue(followup.due_date, followup.status) ? 'bg-red-50' : ''}`} data-testid={`followup-row-${followup.followup_id}`}>
                      <td className="px-4 py-3">
                        <span className="font-medium text-slate-800">{followup.customer_name}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-slate-600">{followup.project_name || '—'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-slate-600">{followup.assigned_rep_name || '—'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-slate-700">{followup.next_action}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-sm ${isOverdue(followup.due_date, followup.status) ? 'text-red-600 font-medium' : 'text-slate-600'}`}>
                          {formatDate(followup.due_date)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <Select value={followup.status} onValueChange={(val) => handleStatusChange(followup, val)}>
                          <SelectTrigger className={`h-7 text-xs ${STATUS_COLORS[followup.status] || ''}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Pending">Pending</SelectItem>
                            <SelectItem value="Done">Done</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(followup)}>
                            <Edit2 size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => { setFollowupToDelete(followup); setDeleteDialogOpen(true); }}>
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-2">
              {followups.map((followup) => (
                <Card key={followup.followup_id} className={`p-4 ${isOverdue(followup.due_date, followup.status) ? 'border-red-200 bg-red-50' : ''}`}>
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-medium text-slate-800">{followup.customer_name}</p>
                      {followup.project_name && <p className="text-xs text-blue-600">{followup.project_name}</p>}
                    </div>
                    <Badge className={STATUS_COLORS[followup.status]}>{followup.status}</Badge>
                  </div>
                  <p className="text-sm text-slate-700 mb-2">{followup.next_action}</p>
                  <div className="flex flex-wrap gap-2 text-xs text-slate-500 mb-3">
                    {followup.assigned_rep_name && <span className="flex items-center gap-1"><User size={12} /> {followup.assigned_rep_name}</span>}
                    <span className={`flex items-center gap-1 ${isOverdue(followup.due_date, followup.status) ? 'text-red-600' : ''}`}>
                      <Calendar size={12} /> {formatDate(followup.due_date)}
                    </span>
                  </div>
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(followup)}>
                      <Edit2 size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => { setFollowupToDelete(followup); setDeleteDialogOpen(true); }}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Followup Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingFollowup ? 'Edit Followup' : 'New Followup'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-slate-600 mb-1 block">Customer Name *</label>
              <Input 
                value={form.customer_name}
                onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                placeholder="Customer name"
                data-testid="followup-customer-input"
              />
            </div>
            <div>
              <label className="text-sm text-slate-600 mb-1 block">Project</label>
              <Input 
                value={form.project_name}
                onChange={(e) => setForm({ ...form, project_name: e.target.value })}
                placeholder="Project name"
              />
            </div>
            <div>
              <label className="text-sm text-slate-600 mb-1 block">Assigned Rep</label>
              <Select value={form.assigned_rep_id || "none"} onValueChange={(val) => setForm({ ...form, assigned_rep_id: val === "none" ? "" : val })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select rep" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select rep</SelectItem>
                  {teamMembers.map((m) => (
                    <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-slate-600 mb-1 block">Next Action *</label>
              <Input 
                value={form.next_action}
                onChange={(e) => setForm({ ...form, next_action: e.target.value })}
                placeholder="What needs to be done?"
                data-testid="followup-action-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-slate-600 mb-1 block">Due Date</label>
                <Input 
                  type="date"
                  value={form.due_date}
                  onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                />
              </div>
              {editingFollowup && (
                <div>
                  <label className="text-sm text-slate-600 mb-1 block">Status</label>
                  <Select value={form.status} onValueChange={(val) => setForm({ ...form, status: val })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pending">Pending</SelectItem>
                      <SelectItem value="Done">Done</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div>
              <label className="text-sm text-slate-600 mb-1 block">Notes</label>
              <Textarea 
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Additional notes"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} data-testid="save-followup-btn">{editingFollowup ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Followup</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this followup for "{followupToDelete?.customer_name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ==================== DIARY SECTION ====================
const DiarySection = ({ user }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  
  // Form state
  const [form, setForm] = useState({
    title: '',
    content: '',
    tags: []
  });

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      
      const response = await api.getManagerDiary(params);
      setNotes(response.data.notes || []);
    } catch (error) {
      toast.error('Failed to load notes');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const handleSubmit = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    
    try {
      if (editingNote) {
        await api.updateManagerDiaryNote(editingNote.note_id, form);
        toast.success('Note updated');
      } else {
        await api.createManagerDiaryNote(form);
        toast.success('Note created');
      }
      setDialogOpen(false);
      setEditingNote(null);
      setForm({ title: '', content: '', tags: [] });
      fetchNotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save note');
    }
  };

  const handleEdit = (note) => {
    setEditingNote(note);
    setForm({
      title: note.title || '',
      content: note.content || '',
      tags: note.tags || []
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!noteToDelete) return;
    try {
      await api.deleteManagerDiaryNote(noteToDelete.note_id);
      toast.success('Note deleted');
      setDeleteDialogOpen(false);
      setNoteToDelete(null);
      fetchNotes();
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-4">
      {/* Private Notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2 text-sm text-amber-700">
        <AlertTriangle size={16} />
        <span>Your diary notes are private. Only you can see them.</span>
      </div>

      {/* Search and Add Button */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input 
            placeholder="Search notes..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="diary-search"
          />
        </div>
        
        <Button onClick={() => { setEditingNote(null); setForm({ title: '', content: '', tags: [] }); setDialogOpen(true); }} data-testid="add-note-btn">
          <Plus size={16} className="mr-1" /> Add Note
        </Button>
      </div>

      {/* Notes List */}
      <div className="space-y-3">
        {loading ? (
          <div className="text-center py-8">
            <RefreshCw className="animate-spin mx-auto mb-2 text-slate-400" size={24} />
            <p className="text-slate-500">Loading notes...</p>
          </div>
        ) : notes.length === 0 ? (
          <Card className="p-8 text-center">
            <BookOpen size={40} className="mx-auto mb-2 text-slate-400" />
            <p className="text-slate-600">No diary notes yet</p>
            <p className="text-sm text-slate-400">Start writing your observations and insights</p>
          </Card>
        ) : (
          notes.map((note) => (
            <Card key={note.note_id} className="p-4" data-testid={`note-card-${note.note_id}`}>
              <div className="flex justify-between items-start mb-2">
                <div>
                  <p className="font-medium text-slate-800">{note.title}</p>
                  <p className="text-xs text-slate-400">{formatDateTime(note.created_at)}</p>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(note)}>
                    <Edit2 size={14} />
                  </Button>
                  <Button variant="ghost" size="sm" className="text-red-600" onClick={() => { setNoteToDelete(note); setDeleteDialogOpen(true); }}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
              <p className="text-sm text-slate-600 whitespace-pre-wrap">{note.content}</p>
              {note.tags && note.tags.length > 0 && (
                <div className="flex gap-1 mt-3">
                  {note.tags.map((tag, idx) => (
                    <Badge key={idx} variant="outline" className="text-xs">{tag}</Badge>
                  ))}
                </div>
              )}
            </Card>
          ))
        )}
      </div>

      {/* Note Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingNote ? 'Edit Note' : 'New Note'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-slate-600 mb-1 block">Title *</label>
              <Input 
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Note title"
                data-testid="note-title-input"
              />
            </div>
            <div>
              <label className="text-sm text-slate-600 mb-1 block">Content *</label>
              <Textarea 
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                placeholder="Write your thoughts..."
                rows={6}
                data-testid="note-content-input"
              />
            </div>
            <div>
              <label className="text-sm text-slate-600 mb-1 block">Tags (comma separated)</label>
              <Input 
                value={form.tags.join(', ')}
                onChange={(e) => setForm({ ...form, tags: e.target.value.split(',').map(t => t.trim()).filter(t => t) })}
                placeholder="team, strategy, feedback"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} data-testid="save-note-btn">{editingNote ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Note</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{noteToDelete?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ==================== MAIN COMPONENT ====================
const ManagerDesk = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'tasks');
  const [summary, setSummary] = useState(null);
  const [showAlert, setShowAlert] = useState(true);

  // Fetch summary for alert banner
  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const response = await api.getManagerTasksSummary();
        setSummary(response.data);
      } catch (error) {
        console.error('Failed to load summary');
      }
    };
    fetchSummary();
  }, [activeTab]); // Refresh when tab changes

  // Update URL when tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  // Handle alert click - navigate to relevant filter
  const handleAlertClick = (type) => {
    if (type === 'overdue') {
      setActiveTab('tasks');
      setSearchParams({ tab: 'tasks', filter: 'overdue' });
    } else if (type === 'today') {
      setActiveTab('tasks');
      setSearchParams({ tab: 'tasks', filter: 'today' });
    }
    setShowAlert(false);
  };

  // Tab config
  const tabs = [
    { id: 'tasks', label: 'Tasks', icon: ClipboardList },
    { id: 'followups', label: 'Followups', icon: CalendarCheck },
    { id: 'diary', label: 'Diary', icon: BookOpen }
  ];

  // Calculate alert message
  const getAlertMessage = () => {
    if (!summary) return null;
    const parts = [];
    if (summary.total_overdue > 0) {
      parts.push(`${summary.total_overdue} overdue`);
    }
    if (summary.total_due_today > 0) {
      parts.push(`${summary.total_due_today} due today`);
    }
    return parts.length > 0 ? parts.join(', ') : null;
  };

  const alertMessage = getAlertMessage();

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 sticky top-0 z-10 shadow-sm">
        <div className="flex items-center gap-3 max-w-6xl mx-auto">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-100 rounded-xl">
            <ArrowLeft size={20} className="text-slate-600" />
          </button>
          <div className="flex-1">
            <h1 className="font-bold text-[#111827]">Manager Desk</h1>
            <p className="text-xs text-slate-500">Tasks, Followups & Notes</p>
          </div>
        </div>
      </div>

      {/* Alert Banner - Shows overdue/due today items */}
      {showAlert && alertMessage && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <div 
              className="flex items-center gap-2 cursor-pointer flex-1"
              onClick={() => handleAlertClick(summary?.total_overdue > 0 ? 'overdue' : 'today')}
              data-testid="alert-banner"
            >
              <AlertTriangle size={16} className="text-amber-600" />
              <span className="text-sm text-amber-800 font-medium">
                {alertMessage}
              </span>
              <ChevronRight size={14} className="text-amber-600" />
            </div>
            <button 
              onClick={(e) => { e.stopPropagation(); setShowAlert(false); }}
              className="p-1 hover:bg-amber-100 rounded"
            >
              <X size={14} className="text-amber-600" />
            </button>
          </div>
        </div>
      )}

      <div className="flex max-w-6xl mx-auto">
        {/* Left Navigation - Desktop */}
        <div className="hidden md:block w-56 bg-white border-r border-slate-200 min-h-[calc(100vh-64px)] p-4">
          <nav className="space-y-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === tab.id 
                      ? 'bg-blue-50 text-blue-700' 
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                  data-testid={`nav-${tab.id}`}
                >
                  <Icon size={18} />
                  {tab.label}
                </button>
              );
            })}
          </nav>
          
          {/* Today's Focus Summary - Desktop */}
          {summary && (summary.total_due_today > 0 || summary.total_overdue > 0) && (
            <div className="mt-6 p-3 bg-slate-50 rounded-lg">
              <p className="text-xs font-semibold text-slate-600 mb-2">TODAY'S FOCUS</p>
              {summary.total_overdue > 0 && (
                <div 
                  className="flex items-center justify-between text-sm mb-1 cursor-pointer hover:bg-slate-100 rounded px-2 py-1 -mx-2"
                  onClick={() => handleAlertClick('overdue')}
                >
                  <span className="text-red-600">Overdue</span>
                  <Badge className="bg-red-100 text-red-700 text-xs">{summary.total_overdue}</Badge>
                </div>
              )}
              {summary.total_due_today > 0 && (
                <div 
                  className="flex items-center justify-between text-sm cursor-pointer hover:bg-slate-100 rounded px-2 py-1 -mx-2"
                  onClick={() => handleAlertClick('today')}
                >
                  <span className="text-amber-600">Due Today</span>
                  <Badge className="bg-amber-100 text-amber-700 text-xs">{summary.total_due_today}</Badge>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Mobile Tab Navigation */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-20">
          <div className="flex justify-around py-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex flex-col items-center gap-1 px-4 py-2 ${
                    activeTab === tab.id ? 'text-blue-600' : 'text-slate-500'
                  }`}
                >
                  <Icon size={20} />
                  <span className="text-xs">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-4 pb-24 md:pb-4">
          {activeTab === 'tasks' && <TasksSection user={user} initialFilter={searchParams.get('filter')} />}
          {activeTab === 'followups' && <FollowupsSection user={user} initialFilter={searchParams.get('filter')} />}
          {activeTab === 'diary' && <DiarySection user={user} />}
        </div>
      </div>
    </div>
  );
};

export default ManagerDesk;
